import { HoverTooltip } from "@/components/core/HoverTooltip";
import { BookmarkIconButton } from "@/components/core/inputs/BookmarkIconButton";
import { DownloadIconButton } from "@/components/core/inputs/DownloadIconButton";
import { cn } from "@/lib/utils";
import { useArchiveBookmark } from "hooks/useArchiveBookmark";
import { useAuth } from "hooks/useAuth";
import { ArchiveIconButton } from "lib/icons";
import Image from "next/image";
import { ComponentProps, useState } from "react";
import { getGoogleDriveThumbnailUrl } from "utils/googleDrive";

import {
    ContentCardLayout,
    ContentThumbnailImg,
    ContentTitle
} from "../contents/cards/ContentCardLayout";
import { ArchiveTags } from "./ArchiveTags";

type ArchiveItem = {
    id: string;
    title: string;
    source: string;
    sourcename: string;
    mix_archive_sourcename?: {
        imgurl?: string | null;
        source: string;
        sourcename: string;
    } | null;
    view_url: string;
    download_url: string;
    thumbnail_url?: string | null;
    tags?: Array<string> | null;
    created_at?: any | null;
    updated_at?: any | null;
    bookmarked?: boolean;
};

// Calculate time difference for "X일전" display
const getTimeAgo = (date: any) => {
    if (!date) return "";
    const now = new Date();
    const past = new Date(date);
    const diffInDays = Math.floor(
        (now.getTime() - past.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffInDays === 0) return "오늘";
    if (diffInDays === 1) return "1일전";
    return `${diffInDays}일전`;
};

export const ArchiveVerticalCard = ({
    archive,
    tagEnums,
    onClick,
    onDownload,
    isGuest = false,
    className,
    ...props
}: Omit<ComponentProps<typeof ContentCardLayout>, "children"> & {
    archive: ArchiveItem;
    tagEnums: Array<{ value: string; comment?: string | null }>;
    onClick?: () => void;
    onDownload?: (e: React.MouseEvent) => void;
    isGuest?: boolean;
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isDownloadClicked, setIsDownloadClicked] = useState(false);
    const [showFallback, setShowFallback] = useState(false);
    const { toggle: toggleBookmark } = useArchiveBookmark(archive);
    const { openSignInModal } = useAuth();

    // Generate thumbnail from view_url if thumbnail_url is not available
    const thumbnailUrl =
        archive.thumbnail_url ||
        (archive.view_url
            ? getGoogleDriveThumbnailUrl(archive.view_url, "w1920")
            : null);

    const handleDownload = (e: React.MouseEvent) => {
        e.stopPropagation();

        if (isGuest) {
            openSignInModal();
            return;
        }

        // Show orange color momentarily
        setIsDownloadClicked(true);
        setTimeout(() => {
            setIsDownloadClicked(false);
        }, 200);

        if (onDownload) {
            onDownload(e);
        } else {
            window.open(archive.download_url, "_blank");
        }
    };

    const handleBookmark = (e: React.MouseEvent) => {
        e.stopPropagation();
        toggleBookmark();
    };

    return (
        <ContentCardLayout
            className={cn("group", className)}
            {...props}
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div
                className="relative mb-3 h-[180px] w-full overflow-hidden
                    rounded-xl"
            >
                <ContentThumbnailImg
                    className="transition-transform duration-300 ease-out
                        group-hover:scale-105"
                    src={thumbnailUrl ?? ""}
                    unoptimized
                    fill={true}
                    onFallbackChange={setShowFallback}
                />
                {!isHovered ? (
                    <ArchiveIconButton
                        className="absolute right-[10px] top-[10px] z-[1] h-8
                            w-8"
                    />
                ) : (
                    <div
                        className="animate-slide-in-from-right absolute
                            right-[10px] top-[10px] z-[1] flex gap-2"
                    >
                        <div
                            className="animate-button-slide-in-delay-1 group
                                relative opacity-0"
                        >
                            <HoverTooltip
                                tooltipClassName="bottom-[-32px] left-1/2 -translate-x-[85%] before:right-[10px]"
                                content="다운로드"
                            >
                                <div
                                    onClick={handleDownload}
                                    className="cursor-pointer"
                                >
                                    <DownloadIconButton
                                        variant="filled"
                                        checked={isDownloadClicked}
                                        onChange={() => {}}
                                    />
                                </div>
                            </HoverTooltip>
                        </div>
                        <div
                            className="animate-button-slide-in-delay-2 relative
                                opacity-0"
                        >
                            <div
                                onClick={handleBookmark}
                                className="cursor-pointer"
                            >
                                <BookmarkIconButton
                                    variant="filled"
                                    checked={archive.bookmarked || false}
                                    onChange={() => {}}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {archive.sourcename && (
                <header className="flex items-center justify-between gap-2">
                    <Image
                        src={
                            archive.mix_archive_sourcename?.imgurl ||
                            "https://cdn.mix.day/img/mix.png"
                        }
                        alt={archive.sourcename}
                        width={24}
                        height={24}
                        className="rounded-sm"
                        unoptimized
                        onError={(e) => {
                            e.currentTarget.style.display = "none";
                        }}
                    />
                    <div
                        className="flex min-w-0 flex-1 items-center
                            justify-between"
                    >
                        <div className="text-[13px] font-normal leading-[140%]">
                            {archive.sourcename}
                        </div>

                        {archive.created_at && (
                            <div className="text-xs text-text-secondary">
                                {getTimeAgo(archive.created_at)}
                            </div>
                        )}
                    </div>
                </header>
            )}

            <ContentTitle
                className="mb-4 mt-3 line-clamp-2 break-keep text-base
                    font-semibold leading-[140%]"
            >
                {archive.title}
            </ContentTitle>

            {archive.tags && archive.tags.length > 0 && (
                <ArchiveTags tags={archive.tags} tagEnums={tagEnums} />
            )}
        </ContentCardLayout>
    );
};
